import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { Button, Card, CardBody, Chip, Select, SelectItem, Spinner } from '@structural-climbing/ui'
import { PageHeader } from '../components/PageHeader'
import { MemberIdentity } from '../features/members/MemberIdentity'
import { MemberProfileModal } from '../features/members/MemberProfileModal'
import { ProductSalesCard } from '../features/products/ProductSalesCard'
import { ExternalProviderCard } from '../features/statistics/ExternalProviderCard'
import { RevenueExportModal } from '../features/statistics/RevenueExportModal'
import { QuestionCard } from '../features/statistics/insights/QuestionCard'
import { buildAnswers, type AnswerTone } from '../features/statistics/insights/answers'
import {
  BarList,
  Columns,
  Headline,
  Heatmap,
  Legend,
  SERIES,
  SplitBar,
  TrendLine,
} from '../features/statistics/insights/charts'
import type { GymInsights } from '../features/statistics/insights/types'

const MONTH_CHOICES = [3, 6, 12] as const
const PAYMENT_METHODS = [
  { key: 'card', color: SERIES.blue },
  { key: 'cash', color: SERIES.terracotta },
  { key: 'online', color: SERIES.aqua },
  { key: 'transfer', color: SERIES.violet },
] as const
const TONE_DOT: Record<AnswerTone, string> = {
  good: 'bg-success-500',
  warn: 'bg-warning-500',
  info: 'bg-default-400',
}
const AT_RISK_SHOWN = 5

/** The last 12 calendar months as YYYY-MM, newest first: what the export offers. */
function lastMonths(count: number): string[] {
  const now = new Date()
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
  })
}
function monthBefore(ym: string): string {
  const [y, m] = ym.split('-').map(Number)
  const d = new Date(y, m - 2, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

/**
 * Statistics: one page of questions an owner asks, each answered by a card, with
 * the plain answers in a column on the left. Everything comes from one call to
 * /gym-insights.
 */
export function StatisticsPage() {
  const { getAccessTokenSilently } = useAuth()
  const { t, i18n } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string
  const lang = i18n.language

  const [months, setMonths] = useState<number>(12)
  const [data, setData] = useState<GymInsights | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [profileTarget, setProfileTarget] = useState<string | null>(null)
  const [showAllAtRisk, setShowAllAtRisk] = useState(false)
  const [exportMonth, setExportMonth] = useState(lastMonths(1)[0])
  const [exportOpen, setExportOpen] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(false)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/gym-insights?months=${months}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (res.ok) setData((await res.json()) as GymInsights)
      else setError(true)
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [getAccessTokenSilently, apiUrl, months])

  useEffect(() => {
    void load()
  }, [load])

  const monthShort = useCallback(
    (ym: string) => new Date(`${ym}-15T12:00:00`).toLocaleDateString(lang, { month: 'short' }),
    [lang],
  )
  const monthLong = useCallback(
    (ym: string) =>
      new Date(`${ym}-15T12:00:00`).toLocaleDateString(lang, { month: 'long', year: 'numeric' }),
    [lang],
  )
  const currency = data?.currency ?? 'RON'
  const money = useCallback(
    (v: number) =>
      new Intl.NumberFormat(lang, { style: 'currency', currency, maximumFractionDigits: 0 }).format(
        v,
      ),
    [lang, currency],
  )
  const num = (v: number) => v.toLocaleString(lang)

  const answers = useMemo(
    () => (data ? buildAnswers(data, t, (ym) => monthLong(ym), money) : []),
    [data, t, monthLong, money],
  )

  const monthChips = (
    <div className="flex gap-1.5">
      {MONTH_CHOICES.map((m) => (
        <Button
          key={m}
          size="sm"
          radius="full"
          variant={months === m ? 'solid' : 'flat'}
          color={months === m ? 'primary' : 'default'}
          className="font-semibold"
          aria-pressed={months === m}
          onPress={() => setMonths(m)}
        >
          {t('statistics.insights.monthsChoice', { count: m })}
        </Button>
      ))}
    </div>
  )

  return (
    <div className="p-4 sm:p-8">
      <PageHeader
        title={t('statistics.title')}
        subtitle={t('statistics.insights.subtitle', { count: months })}
        actions={monthChips}
      />

      {loading && !data && (
        <div className="flex items-center gap-2 text-sm text-foreground/50">
          <Spinner size="sm" />
          <span>{t('statistics.loading')}</span>
        </div>
      )}
      {!loading && error && <p className="text-sm text-danger">{t('statistics.errorLoad')}</p>}

      {data && (
        <StatisticsBody
          data={data}
          labels={data.months.map((m) => monthShort(m.month))}
          answers={answers}
          money={money}
          num={num}
          monthLong={monthLong}
          onOpenProfile={setProfileTarget}
          showAllAtRisk={showAllAtRisk}
          onToggleAtRisk={() => setShowAllAtRisk((v) => !v)}
          exportCard={
            <Card shadow="sm">
              <CardBody className="flex flex-col gap-3 px-4 py-4">
                <h2 className="text-[15px] font-semibold">
                  {t('statistics.insights.exportTitle')}
                </h2>
                <p className="text-xs text-foreground/55">{t('statistics.insights.exportHint')}</p>
                <Select
                  size="sm"
                  aria-label={t('statistics.insights.exportMonth')}
                  label={t('statistics.insights.exportMonth')}
                  selectedKeys={[exportMonth]}
                  disallowEmptySelection
                  onSelectionChange={(keys) => {
                    const v = [...(keys as Iterable<React.Key>)][0]
                    if (v) setExportMonth(String(v))
                  }}
                >
                  {lastMonths(12).map((ym) => (
                    <SelectItem key={ym} textValue={monthLong(ym)}>
                      {monthLong(ym)}
                    </SelectItem>
                  ))}
                </Select>
                <Button variant="bordered" className="border-1" onPress={() => setExportOpen(true)}>
                  {t('statistics.revenueExportTitle')}
                </Button>
              </CardBody>
            </Card>
          }
        />
      )}

      {profileTarget && (
        <MemberProfileModal userId={profileTarget} onClose={() => setProfileTarget(null)} />
      )}
      {exportOpen && (
        <RevenueExportModal
          onClose={() => setExportOpen(false)}
          periodParams={{
            mode: 'fixed',
            unit: 'month',
            a: exportMonth,
            b: monthBefore(exportMonth),
          }}
          currency={currency}
          periodLabel={monthLong(exportMonth)}
        />
      )}
    </div>
  )
}

interface BodyProps {
  data: GymInsights
  labels: string[]
  answers: ReturnType<typeof buildAnswers>
  money: (v: number) => string
  num: (v: number) => string
  monthLong: (ym: string) => string
  onOpenProfile: (userId: string) => void
  showAllAtRisk: boolean
  onToggleAtRisk: () => void
  exportCard: React.ReactNode
}

function StatisticsBody({
  data,
  labels,
  answers,
  money,
  num,
  monthLong,
  onOpenProfile,
  showAllAtRisk,
  onToggleAtRisk,
  exportCard,
}: BodyProps) {
  const { t, i18n } = useTranslation()
  const months = data.months
  const last = months[months.length - 1]
  const full = months.slice(0, -1)
  const prev = full[full.length - 1] ?? last
  const first = months[0]
  const pctOf = (a: number, b: number) => (b > 0 ? Math.round((a / b) * 100) : null)

  const latestEnded = [...full].reverse().find((m) => m.ended > 0)
  const latestFirst = [...full].reverse().find((m) => m.firstVisits > 0)
  const tenureMonths = (() => {
    const sorted = [...data.tenureDays].sort((a, b) => a - b)
    if (sorted.length === 0) return null
    return Math.round(sorted[Math.floor(sorted.length / 2)] / 30)
  })()
  const tenureBuckets = [
    { key: 'under1', test: (d: number) => d < 30 },
    { key: 'oneToThree', test: (d: number) => d >= 30 && d < 91 },
    { key: 'threeToSix', test: (d: number) => d >= 91 && d < 182 },
    { key: 'sixToTwelve', test: (d: number) => d >= 182 && d < 365 },
    { key: 'overYear', test: (d: number) => d >= 365 },
  ].map((b) => ({
    label: t(`statistics.insights.tenure.${b.key}`),
    value: data.tenureDays.filter(b.test).length,
  }))
  const ageRows = (['under13', 'teen', 'from18', 'from25', 'from35', 'from45'] as const).map(
    (k) => ({
      label: t(`statistics.insights.age.${k}`),
      values: [data.ages[k]],
    }),
  )
  const visitRows = (
    ['none', 'one', 'twoToThree', 'fourToSeven', 'eightToEleven', 'twelvePlus'] as const
  ).map((k) => ({ label: t(`statistics.insights.visits.${k}`), values: [data.visitsPerMember[k]] }))
  const hours = (() => {
    const seen = data.busyHours.map((c) => c.hour)
    const lo = seen.length ? Math.min(...seen, 10) : 8
    const hi = seen.length ? Math.max(...seen, 21) : 22
    return Array.from({ length: hi - lo + 1 }, (_, i) => lo + i)
  })()
  const weekdays = Array.from({ length: 7 }, (_, i) =>
    // 2024-01-01 was a Monday.
    new Date(2024, 0, 1 + i).toLocaleDateString(i18n.language, { weekday: 'short' }),
  )
  const atRiskShown = showAllAtRisk ? data.atRisk : data.atRisk.slice(0, AT_RISK_SHOWN)
  const methodNames = PAYMENT_METHODS.map((m) => t(`invoices.${m.key}`))
  const last30 = t('statistics.insights.last30Days')

  return (
    <div className="grid items-start gap-4 lg:grid-cols-[minmax(240px,300px)_minmax(0,1fr)]">
      <aside className="flex flex-col gap-4 lg:sticky lg:top-4">
        <Card shadow="sm">
          <CardBody className="flex flex-col gap-3 px-4 py-4">
            <h2 className="text-[15px] font-semibold">{t('statistics.insights.inShort')}</h2>
            <ul className="flex flex-col gap-2.5">
              {answers.map((a) => (
                <li key={a.lead + a.text} className="flex gap-2.5 text-[13px] leading-snug">
                  <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${TONE_DOT[a.tone]}`} />
                  <span>
                    <span className="font-semibold">{a.lead}</span> {a.text}
                  </span>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
        {exportCard}
      </aside>

      <div className="grid gap-4 md:grid-cols-2">
        <QuestionCard
          question={t('statistics.insights.growing.q')}
          answers={t('statistics.insights.growing.a')}
          source={t('statistics.insights.src.subscriptions')}
        >
          <Headline
            value={num(data.membersNow)}
            note={t('statistics.insights.growing.note', {
              diff: signed(data.membersNow - first.members),
              count: months.length,
            })}
            tone={data.membersNow >= first.members ? 'good' : 'bad'}
          />
          <TrendLine
            labels={labels}
            fill
            series={[
              {
                name: t('statistics.insights.growing.series'),
                color: SERIES.terracotta,
                values: months.map((m) => m.members),
              },
            ]}
            format={num}
          />
        </QuestionCard>

        <QuestionCard
          question={t('statistics.insights.sources.q')}
          answers={t('statistics.insights.sources.a')}
          source={t('statistics.insights.src.sources')}
        >
          <Headline
            value={num(prev.joined)}
            note={t('statistics.insights.sources.note', { month: monthLong(prev.month) })}
          />
          <Columns
            rows={months.map((m, i) => ({
              label: labels[i],
              values: [m.sources.app, m.sources.desk, m.sources.dayPass],
            }))}
            colors={[SERIES.blue, SERIES.terracotta, SERIES.aqua]}
            names={[
              t('statistics.insights.sources.app'),
              t('statistics.insights.sources.desk'),
              t('statistics.insights.sources.dayPass'),
            ]}
            format={num}
          />
          <Legend
            items={[
              { name: t('statistics.insights.sources.app'), color: SERIES.blue },
              { name: t('statistics.insights.sources.desk'), color: SERIES.terracotta },
              { name: t('statistics.insights.sources.dayPass'), color: SERIES.aqua },
            ]}
          />
        </QuestionCard>

        <QuestionCard
          question={t('statistics.insights.comeback.q')}
          answers={t('statistics.insights.comeback.a')}
          source={t('statistics.insights.src.checkIns')}
        >
          <Headline
            value={latestFirst ? `${pctOf(latestFirst.cameBack, latestFirst.firstVisits)}%` : '—'}
            note={
              latestFirst
                ? t('statistics.insights.comeback.note', {
                    count: latestFirst.firstVisits,
                    month: monthLong(latestFirst.month),
                  })
                : undefined
            }
          />
          <TrendLine
            labels={labels}
            min={0}
            series={[
              {
                name: t('statistics.insights.comeback.series'),
                color: SERIES.terracotta,
                values: months.map((m) => pctOf(m.cameBack, m.firstVisits)),
              },
            ]}
            format={(v) => `${v}%`}
          />
        </QuestionCard>

        <QuestionCard
          question={t('statistics.insights.risk.q')}
          aside={t('statistics.insights.risk.count', { count: data.atRiskCount })}
          answers={t('statistics.insights.risk.a')}
          source={t('statistics.insights.src.risk')}
        >
          {data.atRisk.length === 0 ? (
            <p className="py-4 text-center text-sm text-foreground/50">
              {t('statistics.insights.risk.none')}
            </p>
          ) : (
            <div className="flex flex-col divide-y divide-divider">
              {atRiskShown.map((m) => (
                <button
                  key={m.userId}
                  type="button"
                  className="flex items-center gap-3 py-2 text-left"
                  title={t('members.viewProfile')}
                  onClick={() => onOpenProfile(m.userId)}
                >
                  <span className="min-w-0 flex-1">
                    <MemberIdentity
                      userId={m.userId}
                      name={m.name}
                      nickname={m.nickname}
                      email={m.email}
                      avatarExt={m.avatarExt}
                      avatarUpdatedAt={m.avatarUpdatedAt}
                    />
                  </span>
                  <Chip size="sm" variant="flat" color="warning" className="shrink-0">
                    {m.daysSinceLastVisit === null
                      ? t('statistics.insights.risk.notIn90')
                      : t('statistics.insights.risk.days', { count: m.daysSinceLastVisit })}
                  </Chip>
                </button>
              ))}
              {data.atRisk.length > AT_RISK_SHOWN && (
                <div className="pt-1.5">
                  <Button size="sm" variant="light" onPress={onToggleAtRisk}>
                    {showAllAtRisk
                      ? t('common.showLess')
                      : t('common.showMore', { count: data.atRisk.length - AT_RISK_SHOWN })}
                  </Button>
                </div>
              )}
            </div>
          )}
        </QuestionCard>

        <QuestionCard
          question={t('statistics.insights.renew.q')}
          answers={t('statistics.insights.renew.a')}
          source={t('statistics.insights.src.subscriptions')}
        >
          <Headline
            value={latestEnded ? `${pctOf(latestEnded.renewed, latestEnded.ended)}%` : '—'}
            note={
              latestEnded
                ? t('statistics.insights.renew.note', {
                    count: latestEnded.ended,
                    month: monthLong(latestEnded.month),
                  })
                : undefined
            }
          />
          <TrendLine
            labels={labels}
            min={0}
            series={[
              {
                name: t('statistics.insights.renew.series'),
                color: SERIES.blue,
                values: months.map((m) => pctOf(m.renewed, m.ended)),
              },
            ]}
            format={(v) => `${v}%`}
          />
        </QuestionCard>

        <QuestionCard
          question={t('statistics.insights.tenure.q')}
          answers={t('statistics.insights.tenure.a')}
          source={t('statistics.insights.src.subscriptions')}
        >
          <Headline
            value={
              tenureMonths === null
                ? '—'
                : t('statistics.insights.tenure.months', { count: tenureMonths })
            }
            note={t('statistics.insights.tenure.note')}
          />
          <BarList rows={tenureBuckets} format={num} />
        </QuestionCard>

        <QuestionCard
          question={t('statistics.insights.who.q')}
          answers={t('statistics.insights.who.a', { count: data.ages.unknown })}
          source={t('statistics.insights.src.profiles')}
        >
          <SplitBar
            parts={[
              {
                name: t('statistics.insights.who.adults'),
                value: data.membersNow - data.childrenNow,
                color: SERIES.terracotta,
              },
              {
                name: t('statistics.insights.who.children'),
                value: data.childrenNow,
                color: SERIES.blue,
              },
            ]}
          />
          <Columns
            rows={ageRows}
            colors={[SERIES.terracotta]}
            highlightMax
            showAllLabels
            format={num}
          />
        </QuestionCard>

        <QuestionCard
          question={t('statistics.insights.often.q')}
          answers={t('statistics.insights.often.a', { count: data.visitsPerMember.none })}
          source={t('statistics.insights.src.checkIns')}
        >
          <Columns
            rows={visitRows}
            colors={[SERIES.terracotta]}
            highlightMax
            showAllLabels
            format={num}
          />
        </QuestionCard>

        <QuestionCard
          question={t('statistics.insights.visits.q')}
          answers={t('statistics.insights.visits.a')}
          source={t('statistics.insights.src.checkIns')}
        >
          <Headline
            value={num(prev.checkIns)}
            note={t('statistics.insights.visits.note', {
              count: prev.visitors,
              month: monthLong(prev.month),
            })}
          />
          <TrendLine
            labels={labels}
            min={0}
            series={[
              {
                name: t('statistics.insights.visits.checkIns'),
                color: SERIES.terracotta,
                values: months.map((m) => m.checkIns),
              },
              {
                name: t('statistics.insights.visits.people'),
                color: SERIES.blue,
                values: months.map((m) => m.visitors),
              },
            ]}
            format={num}
          />
          <Legend
            items={[
              { name: t('statistics.insights.visits.checkIns'), color: SERIES.terracotta },
              { name: t('statistics.insights.visits.people'), color: SERIES.blue },
            ]}
          />
        </QuestionCard>

        <QuestionCard
          question={t('statistics.insights.busy.q')}
          answers={t('statistics.insights.busy.a')}
          source={t('statistics.insights.src.checkIns')}
        >
          <Heatmap
            cells={data.busyHours.map((c) => ({
              weekday: c.weekday,
              hour: c.hour,
              value: c.count / 4,
            }))}
            weekdays={weekdays}
            hours={hours}
            format={(v) => t('statistics.insights.busy.cell', { count: Math.round(v * 10) / 10 })}
            quietLabel={t('statistics.insights.busy.quiet')}
            busyLabel={t('statistics.insights.busy.busy')}
          />
        </QuestionCard>

        <QuestionCard
          question={t('statistics.insights.money.q')}
          answers={t('statistics.insights.money.a')}
          source={t('statistics.insights.src.invoices')}
        >
          <Headline value={money(revenue(prev))} note={monthLong(prev.month)} />
          <Columns
            rows={months.map((m, i) => ({
              label: labels[i],
              values: PAYMENT_METHODS.map((p) => Number(m.revenue[p.key] ?? 0)),
            }))}
            colors={PAYMENT_METHODS.map((p) => p.color)}
            names={methodNames}
            format={money}
          />
          <Legend
            items={PAYMENT_METHODS.map((p, i) => ({ name: methodNames[i], color: p.color }))}
          />
        </QuestionCard>

        <QuestionCard
          question={t('statistics.insights.plans.q')}
          answers={t('statistics.insights.plans.a', { count: months.length })}
          source={t('statistics.insights.src.plans')}
        >
          {data.plans.length === 0 ? (
            <p className="py-4 text-center text-sm text-foreground/50">
              {t('statistics.insights.plans.none')}
            </p>
          ) : (
            <BarList
              rows={data.plans
                .slice(0, 8)
                .map((p) => ({ label: p.name, value: Number(p.revenue) }))}
              format={money}
            />
          )}
        </QuestionCard>

        {data.productSales && (
          <div className="md:col-span-2">
            <ProductSalesCard
              sales={data.productSales}
              currency={data.currency}
              periodLabel={last30}
            />
          </div>
        )}
        {data.externalProviders.map((p) => (
          <ExternalProviderCard
            key={p.provider}
            stats={p}
            currency={data.currency}
            periodLabel={last30}
            comparePeriodLabel={t('statistics.insights.previous30Days')}
            comparePartial={false}
          />
        ))}
      </div>
    </div>
  )
}

function revenue(m: { revenue: Record<string, number> }): number {
  return Object.values(m.revenue).reduce((a, b) => a + Number(b), 0)
}

function signed(n: number): string {
  return n > 0 ? `+${n}` : String(n)
}
